import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NaploService } from '../naplo.service';
import { DataService } from '../../data.service';

/*  NAPLO – „Első beállítás" varázsló (first-install wizard).
 *
 *  Egy ügyfél könyvelésének indulásához öt dolgot kell beállítani. Ez a kártya egy helyen
 *  mutatja, melyik lépés KÉSZ és melyik HIÁNYZIK, és minden lépésről a megfelelő beállító
 *  oldalra visz. Az állapotot NEM mockoljuk: minden lépéshez VALÓS backend-lekérés tartozik
 *  (lásd a lepesek[] `allapot()`-jait), és abból vezetjük le a kész/hiányzik jelzést.
 *
 *  Az aktív ügyfél a NaploService-ből jön (naploAktiv storage), mint a többi NAPLO-oldalon. */

interface WizardLepes {
  kulcs: string;
  cim: string;
  leiras: string;
  ikon: string;        // a lépés tartalmi ikonja (bal oldal)
  route: string;       // a '/naplo/<route>' célútvonal
  query?: Record<string, string>;  // opcionális query-paraméterek (nezet/ful) – a célhely a menüből
  kesz: boolean | null; // null = még töltünk / nem sikerült lekérni
}

@Component({
  selector: 'app-naplo-elso-beallitas',
  templateUrl: './elso-beallitas.component.html',
  styleUrls: ['../naplo-shared.scss', './elso-beallitas.component.scss'],
})
export class ElsoBeallitasComponent implements OnInit {
  get aktiv() { return this.naplo.aktiv; }

  loading = true;

  lepesek: WizardLepes[] = [
    // Nyitó: kettősnél a Naplók → Nyitó fül; egyszeresnél (ngOnInit) a Törzsadatok → Pénztár és bank.
    { kulcs: 'nyito', cim: 'Nyitó napló', ikon: 'play-circle-outline', route: 'naplok', query: { ful: 'nyito' },
      leiras: 'Az év nyitó tételei / nyitó egyenlege rögzítve.', kesz: null },
    { kulcs: 'bank', cim: 'Bank', ikon: 'card-outline', route: 'bank',
      leiras: 'Legalább egy bankszámla felvéve az ügyfélhez.', kesz: null },
    { kulcs: 'kau', cim: 'Ügyfélkapu / KAÜ', ikon: 'mail-open-outline', route: 'fiokok',
      leiras: 'KAÜ-tárhely (Ügyfélkapu+ / Cégkapu) hozzárendelve.', kesz: null },
    { kulcs: 'onkormanyzat', cim: 'Önkormányzat', ikon: 'business-outline', route: 'eonkormanyzat',
      leiras: 'Önkormányzati adószámla-hozzáférés beállítva.', kesz: null },
    { kulcs: 'nav', cim: 'NAV technikai / M2M', ikon: 'shield-checkmark-outline', route: 'torzsek', query: { nezet: 'osa' },
      leiras: 'NAV online számla technikai felhasználó vagy M2M kapcsolat.', kesz: null },
  ];

  constructor(
    private http: HttpClient,
    private naplo: NaploService,
    private data: DataService,
    private router: Router,
  ) {}

  async ngOnInit() {
    await this.naplo.init();
    // A nyitó a könyvelési módtól függ: kettősnél a könyvelőablak, egyszeresnél a napló/pénztár.
    const nyito = this.lepesek.find((l) => l.kulcs === 'nyito');
    if (nyito && this.aktiv.konyveles_mod !== 'kettos') { nyito.route = 'torzsek'; nyito.query = { nezet: 'penztar' }; }
    // A BANK lépés csak akkor kell, ha az ügyfél használ bankot: kettős könyvvitelnél MINDIG,
    // egyszeresnél a „Bank használata" (bank_naplo) pipa szerint. Ha nincs bepipálva, a varázsló
    // ne várja – kivesszük a lépések közül (így a kész/összes számból és a „minden kész"-ből is).
    const bankKell = this.aktiv.konyveles_mod === 'kettos' || !!this.aktiv.bank_naplo;
    if (!bankKell) this.lepesek = this.lepesek.filter((l) => l.kulcs !== 'bank');
    if (this.aktiv.ugyfelId) { await this.allapotFrissit(); }
    else { this.loading = false; }
  }

  /** Hány lépés kész. */
  get keszSzam(): number { return this.lepesek.filter((l) => l.kesz === true).length; }
  get osszesSzam(): number { return this.lepesek.length; }
  get keszArany(): number { return this.osszesSzam ? this.keszSzam / this.osszesSzam : 0; }
  get mindKesz(): boolean { return this.keszSzam === this.osszesSzam; }

  /*  Minden lépés állapotát PÁRHUZAMOSAN kérjük le, és egymástól FÜGGETLENÜL értékeljük:
   *  ha egy végpont hibázik (pl. 403/500), az adott lépés „ismeretlen" marad, de a többi
   *  helyes marad – nem borítja a teljes kártyát. */
  async allapotFrissit() {
    const id = this.aktiv.ugyfelId;
    if (!id) return;
    this.loading = true;
    const ev = this.aktiv.ev;
    const kettos = (this.aktiv.konyveles_mod === 'kettos');

    // Az ellenoriz() soha nem dob (belül elkapja a hibát), így a Promise.all biztonságos.
    await Promise.all([
      this.ellenoriz('nyito', async () => {
        if (kettos) {
          // Kettős könyvvitel: a 'nyito' naplótípus tételei léteznek-e.
          const rows = await this.get<any[]>(`/api/kettos/tetelek/${id}?ev=${ev}&naplo=nyito`);
          return Array.isArray(rows) && rows.length > 0;
        }
        // Egyszeres: van-e pénztár/bank nyitó egyenleggel (naplo_penztar.nyito_egyenleg).
        const p = await this.get<any[]>(`/api/naplo/penztarak/${id}`);
        return Array.isArray(p) && p.length > 0;
      }),
      this.ellenoriz('bank', async () => {
        const rows = await this.get<any[]>(`/api/naplo-bank/${id}/szamlak`);
        return Array.isArray(rows) && rows.length > 0;
      }),
      this.ellenoriz('kau', async () => {
        // KAÜ-tárhely akkor kész, ha van AKTÍV ügyfélkapu-fiók hozzárendelés az ügyfélhez.
        const rows = await this.get<any[]>(`/api/naplo/ugyfelkapu/hozzarendelesek/${id}`);
        return Array.isArray(rows) && rows.some((r) => !!r.aktiv);
      }),
      this.ellenoriz('onkormanyzat', async () => {
        // Aktív önkormányzati hozzárendelés, KAÜ-fiókhoz kötve.
        const rows = await this.get<any[]>(`/api/naplo/onkormanyzat/ugyfel/${id}`);
        return Array.isArray(rows) && rows.some((r) => !!r.aktiv && !!r.fiok_id);
      }),
      this.ellenoriz('nav', async () => {
        // NAV akkor kész, ha az aktív környezetben a technikai felhasználó kész,
        // VAGY van M2M / eBEV kapcsolat beállítva.
        const tech = await this.get<any>(`/api/naplo/nav-tech/${id}`).catch(() => null);
        const techKesz = !!(tech && tech[tech.aktiv] && tech[tech.aktiv].kesz);
        if (techKesz) return true;
        const m2m = await this.get<any>(`/api/naplo/nav-m2m/${id}`).catch(() => null);
        return !!(m2m && m2m.konfiguralt);
      }),
    ]);

    this.loading = false;
  }

  /** Egy lépés állapotának biztonságos kiértékelése; hiba esetén `null` (ismeretlen) marad. */
  private async ellenoriz(kulcs: string, fn: () => Promise<boolean>) {
    const lepes = this.lepesek.find((l) => l.kulcs === kulcs);
    if (!lepes) return;
    try { lepes.kesz = await fn(); }
    catch { lepes.kesz = null; }
  }

  private get<T = any>(path: string): Promise<T> {
    return firstValueFrom(this.http.get<T>(path));
  }

  /** A „Beállítás" gomb: a megfelelő NAPLO-oldalra navigál. */
  beallit(lepes: WizardLepes) {
    this.router.navigate(['/naplo', lepes.route], lepes.query ? { queryParams: lepes.query } : {});
  }
}
